/*
  A component so safely link to the ListingPage of the given listing.

  When the listing is pending approval, the normal ListingPage won't
  work as the listing isn't yet published. This component links to the
  correct pending-approval variant URL or to the normal ListingPage
  based on the listing state.
*/
import { Box } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import { richText } from '../../../util/richText';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL } from '../../../util/types';
import {
  createSlug,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../../util/urlHelpers';
import NamedLink from '../NamedLink/NamedLink';

const MIN_LENGTH_FOR_LONG_WORDS = 16;

const LONG_WORD_CLASS = 'ListingLink_longWord';

const richTitleSx = {
  [`& .${LONG_WORD_CLASS}`]: {
    // 'break-all' is the fallback option; 'break-word' is used if available.
    // Emotion emits both declarations from the array (later one wins where supported).
    wordBreak: ['break-all', 'break-word'],
    hyphens: 'auto',
  },
};

const ListingLink = ({ className = null, listing = null, children = null }) => {
  const intl = useIntl();
  const listingLoaded = listing && listing.id;
  if (!listingLoaded) {
    return null;
  }
  const id = listing.id.uuid;
  const { title, state, deleted } = listing.attributes;

  // A deleted listing keeps its id but loses its attributes, so there is no
  // title to show and its ListingPage is gone — same handling as ActivityFeed
  // and TransactionPage: plain text instead of a link into a 404.
  if (deleted) {
    return (
      <Box component="span">
        {children ? children : intl.formatMessage({ id: 'ListingLink.deletedListing' })}
      </Box>
    );
  }

  const slug = createSlug(title);
  const richTitle = (
    <Box component="span" sx={richTitleSx}>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: LONG_WORD_CLASS,
      })}
    </Box>
  );

  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isDraft = state === LISTING_STATE_DRAFT;
  const category = intl.formatMessage({ id: 'ListingUrlCategory.default' });
  const variant = isPendingApproval
    ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
    : isDraft
      ? LISTING_PAGE_DRAFT_VARIANT
      : null;
  const linkProps = variant
    ? {
      name: 'ListingPageVariant',
      params: { id, slug, variant, category },
    }
    : {
      name: 'ListingPage',
      params: { id, slug },
    };
  return (
    <NamedLink className={className} {...linkProps}>
      {children ? children : richTitle || ''}
    </NamedLink>
  );
};

export default ListingLink;
